import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

export interface OutgoingEmailLogListItem {
  id: string;
  emailScenario: string;
  recipientEmail: string;
  recipientName?: string;
  subject: string;
  isStudioCopy: boolean;
  /** Omitted on older API responses; treat as accepted when missing */
  status?: string;
  statusReason?: string;
  statusUpdatedAt?: string;
  createdAt: string;
}

export interface OutgoingEmailLogsListResponse {
  data: Array<OutgoingEmailLogListItem>;
  total: number;
  page: number;
  limit: number;
}

export interface OutgoingEmailLogDetail {
  id: string;
  emailScenario: string;
  recipientEmail: string;
  recipientName?: string;
  subject: string;
  templateDataJson?: string;
  bodyHtml?: string;
  bodyText?: string;
  isStudioCopy: boolean;
  status?: string;
  statusReason?: string;
  statusUpdatedAt?: string;
  createdAt: string;
}

export interface EmailScenarioOption {
  value: string;
  label: string;
}

export interface OutgoingEmailScenariosResponse {
  scenarios: Array<EmailScenarioOption>;
  deliveryStatuses?: Array<string>;
}

export interface GetOutgoingEmailLogsParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  emailScenario?: string;
  status?: string;
  /** Unified free-text search: parent name, parent email, student name. */
  q?: string;
}

export const getOutgoingEmailLogsAPI = async (
  params?: GetOutgoingEmailLogsParams,
): Promise<OutgoingEmailLogsListResponse> => {
  const response = await baseAPI.get<OutgoingEmailLogsListResponse>(
    'api/outgoing-email-logs',
    { params },
  );
  return response.data;
};

export const getOutgoingEmailLogByIdAPI = async (
  id: string,
): Promise<OutgoingEmailLogDetail> => {
  const safeId = id.trim();
  const response = await baseAPI.get<OutgoingEmailLogDetail>(
    `api/outgoing-email-logs/${safeId}`,
  );
  return response.data;
};

export const getOutgoingEmailScenariosAPI =
  async (): Promise<OutgoingEmailScenariosResponse> => {
    const response = await baseAPI.get<OutgoingEmailScenariosResponse>(
      'api/outgoing-email-logs/scenarios',
    );
    return response.data;
  };

export const getOutgoingEmailLogsOptions = (
  params?: GetOutgoingEmailLogsParams,
) =>
  queryOptions({
    queryKey: QUERY_KEYS.OUTGOING_EMAIL_LOGS(params),
    queryFn: () => getOutgoingEmailLogsAPI(params),
    placeholderData: keepPreviousData,
  });

export const useOutgoingEmailLogs = (params?: GetOutgoingEmailLogsParams) => {
  return useQuery(getOutgoingEmailLogsOptions(params));
};

export const useOutgoingEmailLogDetail = (id: string | null) => {
  const safeId = id?.trim() ?? '';
  return useQuery({
    queryKey: QUERY_KEYS.OUTGOING_EMAIL_LOG_DETAIL(safeId),
    queryFn: () => getOutgoingEmailLogByIdAPI(safeId),
    enabled: safeId.length > 0,
  });
};

export const getOutgoingEmailScenariosOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.OUTGOING_EMAIL_LOG_SCENARIOS(),
    queryFn: getOutgoingEmailScenariosAPI,
    staleTime: 60 * 60 * 1000,
  });

export const useOutgoingEmailScenarios = () => {
  return useQuery(getOutgoingEmailScenariosOptions());
};
